"use client";

/**
 * Customer-facing support widget — Intercom/Channel-Talk-style floating UI.
 * Mounted globally in the locale layout; hides itself on auth/checkout routes.
 */

import { useCallback, useEffect, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSupportConversation } from "@/hooks/useSupportConversation";
import { SupportLauncher } from "./SupportLauncher";
import { SupportPanel } from "./SupportPanel";
import { NotificationConsentDialog } from "@/components/NotificationConsentDialog";
import type { SupportConversation, SupportMessage } from "@/types/support";
import { useAuth } from "@/contexts/AuthContext";

const HIDDEN_ROUTE_PREFIXES = [
  "/checkout",
  "/login",
  "/reset-password",
  "/auth",
  "/verify-phone",
  "/blocked",
  "/error",
  "/unsubscribed",
  // 크루 **공개면**(허브·소개·신청)은 크리에이터/파트너 대상 별도 서비스 면이라 고객 문의
  // 런처가 뜨면 맥락이 섞인다. 크루 **콘솔**(/crew/dashboard 등)은 예외로 보인다 —
  // 크루 알림(승인·요청 결과·커미션 확정)이 이 지원 채팅에 시스템 메시지로 쌓이므로,
  // 콘솔에서 배지를 못 보면 알림을 받을 자리가 없다(CREW_CONSOLE_PREFIXES 참조).
  "/crew",
  // Search results page has its own floating map↔list toggle that conflicts
  // with the launcher position on mobile.
  "/s",
  // Blog/community hub is a content surface — keep it distraction-free.
  "/blog",
  // Product detail (and its sub-routes like /reviews, /unavailable) — the
  // partner inquiry chat button on the booking card is the right channel for
  // product/booking questions, so we don't surface the platform widget here.
  "/products",
  // Messages list + chat room — the user is already in a chat surface, so a
  // floating "chat with support" launcher would overlap real chat UI and
  // confuse the conversation context.
  "/messages",
  // Post-deletion confirmation — closure page, no nav offered.
  "/account-deleted",
  // SNS event gallery has its own mobile "참여하기" floating button; the support
  // launcher would overlap it and the page isn't a support context.
  "/promotions/sns-event",
];

/**
 * 크루 콘솔 경로 — HIDDEN 의 "/crew" 보다 우선해 런처를 보인다. 공개면(/crew,
 * /crew/creators, /crew/partners, /crew/apply)은 목록에 없으므로 그대로 숨는다.
 * CrewConsoleHeader 의 TABS + 프로필과 같은 집합이다.
 */
const CREW_CONSOLE_PREFIXES = [
  "/crew/dashboard",
  "/crew/products",
  "/crew/campaigns",
  "/crew/links",
  "/crew/settlements",
  "/crew/messages",
  "/crew/profile",
];

function stripLocalePrefix(pathname: string): string {
  // /ko/products/... → /products/...
  return pathname.replace(/^\/[a-z]{2}(?:-[A-Z]{2})?(?=\/|$)/, "") || "/";
}

export function SupportWidget() {
  // 로그인 상태는 AuthProvider 에서. 확인 전(loading)에는 비로그인으로 취급하지 않고
  // 단순히 authed=false(대화 조회 안 함)로 둔다 — 확인되면 훅이 다시 불러온다.
  // preferred_language 는 수신 메시지를 고객의 선호 언어로 보여주는 데 쓰인다
  // (지금 보고 있는 [locale] URL 과 무관하게).
  const { status, user } = useAuth();
  const pathname = usePathname() || "/";
  const searchParams = useSearchParams();
  const isMobile = useIsMobile();

  // Push notifications deep-link to ?support=<conversation_id>. Reading the
  // query in the lazy initializer turns this into derived initial state, so
  // we can drop the otherwise-required URL→state useEffect (and its ref
  // guard). useSearchParams returns the same value on the server and the
  // client, so this is hydration-safe.
  const [open, setOpen] = useState<boolean>(() =>
    Boolean(searchParams?.get("support")),
  );
  // Keep the panel mounted briefly after `open=false` so its closing
  // transition can play out before unmount. Initialize from `open` so the
  // mount-delay state line up on first paint without a follow-up effect tick.
  const [render, setRender] = useState<boolean>(() => open);

  const authed = status === "authenticated" && Boolean(user);
  const conv = useSupportConversation({ authed, active: open });

  // 플랫폼 문의 첫 메시지를 보낸 뒤 알림 권한을 contextual 하게 요청한다.
  // (답장이 푸시로 와야 가치가 있는 맥락) NotificationConsentDialog 가 7일
  // 쿨다운·미구독·미거부를 자체 검사하므로 trigger 를 한 번 켜두면 알아서 한
  // 번만 뜨고, 결제 후 프롬프트와도 쿨다운을 공유해 중복되지 않는다.
  const [askNotif, setAskNotif] = useState(false);

  useEffect(() => {
    if (open) {
      // Mounting in response to a parent-driven `open` toggle is the canonical
      // "sync with external prop/state" pattern — the ESLint rule's "avoid
      // setState in effect" guidance targets cascading derived state, not this.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setRender(true);
      return;
    }
    if (!render) return;
    // setTimeout setState is async, so it isn't flagged. Match the longest
    // closing transition (mobile panel slide = 420ms).
    const t = window.setTimeout(() => setRender(false), 420);
    return () => window.clearTimeout(t);
  }, [open, render]);

  // External "open this widget" requests — e.g. /help's contact section
  // dispatches `locarora:open-support` instead of importing the widget.
  // External event → React state is the textbook effect-driven sync, so
  // setOpen is appropriate here even though it's inside an effect handler.
  useEffect(() => {
    const handleOpen = () => setOpen(true);
    window.addEventListener("locarora:open-support", handleOpen);
    return () => window.removeEventListener("locarora:open-support", handleOpen);
  }, []);

  // Lock background body scroll while the full-screen panel is open on mobile.
  // Same pattern used by ProductImageGallery for its full-screen overlay.
  // Desktop's compact panel sits inside the viewport so scroll-through is fine.
  useEffect(() => {
    if (!(open && isMobile)) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open, isMobile]);

  // ALL hooks must be declared before any early return to keep hook order stable
  // across renders (otherwise React throws "Rendered fewer hooks than expected"
  // when navigating between visible and hidden routes).
  const handleConversationCreated = useCallback(
    (created: SupportConversation) => {
      conv.setConversation(created);
    },
    [conv],
  );
  const handleMessageSent = useCallback(
    (msg: SupportMessage) => {
      conv.appendMessage(msg);
      // 메시지를 보낸 사용자에게만 알림 다이얼로그를 마운트한다.
      if (authed) setAskNotif(true);
    },
    [conv, authed],
  );

  // Excluded routes — render nothing (no UI, but hooks above still ran).
  const stripped = stripLocalePrefix(pathname);
  const matches = (prefix: string) =>
    stripped === prefix || stripped.startsWith(`${prefix}/`);
  const hidden =
    HIDDEN_ROUTE_PREFIXES.some(matches) && !CREW_CONSOLE_PREFIXES.some(matches);
  if (hidden) return null;

  // On mobile the panel is full-screen and has its own X button in the header,
  // so the floating launcher would just clutter the modal. Keep it visible on
  // desktop (where it acts as the toggle) and hide it on mobile while open.
  const launcherVisible = !(open && isMobile);

  return (
    <>
      {launcherVisible && (
        <SupportLauncher
          unreadCount={conv.unreadCount}
          open={open}
          onClick={() => setOpen((v) => !v)}
        />
      )}
      {render && (
        <SupportPanel
          authed={authed}
          userId={user?.id ?? null}
          displayName={user?.full_name ?? null}
          preferredLanguage={user?.preferred_language ?? null}
          conversation={conv.conversation}
          messages={conv.messages}
          loading={conv.loading}
          isMobile={isMobile}
          isOpen={open}
          onClose={() => setOpen(false)}
          onConversationCreated={handleConversationCreated}
          onMessageSent={handleMessageSent}
          loginReturnTo={pathname}
          initialTab={searchParams?.get("support") ? "chat" : undefined}
        />
      )}
      {/* 첫 문의 메시지 전송 후에만 마운트 — 다이얼로그가 쿨다운/구독 상태를
          자체 판단해 띄울지 결정한다. */}
      {askNotif && user?.id && (
        <NotificationConsentDialog userId={user.id} />
      )}
    </>
  );
}
